import React from "react";
import { useTranslation } from "react-i18next";
import Layout from '../components/Layout';
import { base44 } from "api/apiClient";
import { useQuery } from "@tanstack/react-query";
import { Users, Euro, Calendar, Receipt } from "lucide-react";
import { format } from "date-fns";

import StatCard from "../components/dashboard/StatCard";
import RevenueForecast from "../components/dashboard/RevenueForecast";
import LowStockAlert from "../components/dashboard/LowStockAlert";
import TaskSummary from "../components/dashboard/TaskSummary";
import useFinanceData from "../hooks/useFinanceData";
import { useRestaurant } from "../contexts/RestaurantContext";
import { parseLocaleNumber } from "utils";

export default function Dashboard() {
  const { t } = useTranslation();
  const { currentRestaurantId } = useRestaurant();
  const restaurantQueryKey = currentRestaurantId ?? 'default';
  const { data: sales } = useQuery({
    queryKey: ['sales', restaurantQueryKey],
    queryFn: () => base44.entities.Sale.list('-date'),
    initialData: [],
  });

  const { data: inventory } = useQuery({
    queryKey: ['inventory', restaurantQueryKey],
    queryFn: () => base44.entities.InventoryItem.list(),
    initialData: [],
  });

  const { data: tasks } = useQuery({
    queryKey: ['tasks', restaurantQueryKey],
    queryFn: () => base44.entities.Task.list('-due_date'),
    initialData: [],
  });

  // Forecast data for today's stat cards
  const { data: forecastResponse } = useQuery({
    queryKey: ['forecast', 'revenue', restaurantQueryKey, 7],
    queryFn: () => base44.forecast.getRevenue(7),
    placeholderData: { forecasts: [], meta: {} },
    staleTime: 5 * 60 * 1000,
  });

  const forecasts = forecastResponse?.forecasts || [];

  // Calculate stats
  const today = format(new Date(), 'yyyy-MM-dd');
  const todayForecast = forecasts.find((forecast) => forecast.date === today) || forecasts[0] || null;
  const todaySales = sales.filter(sale => sale.date === today);
  const todayRevenue = todaySales.reduce((sum, sale) => sum + (sale.total_amount || 0), 0);
  const todayGuests = todaySales.reduce((sum, sale) => sum + (sale.guests_count || 0), 0);

  // Calculate actual trends from data
  const calculateTrends = () => {
    const todayDayOfWeek = new Date().getDay();
    const lastWeekSales = sales.filter(sale => {
      const saleDate = new Date(sale.date);
      const diffDays = (new Date(today) - saleDate) / (1000 * 60 * 60 * 24);
      return diffDays >= 7 && diffDays < 14 && saleDate.getDay() === todayDayOfWeek;
    });

    const lastWeekRevenue = lastWeekSales.reduce((sum, sale) => sum + (sale.total_amount || 0), 0);
    const lastWeekGuests = lastWeekSales.reduce((sum, sale) => sum + (sale.guests_count || 0), 0);

    const revenueTrend = lastWeekRevenue > 0
      ? ((todayRevenue - lastWeekRevenue) / lastWeekRevenue * 100).toFixed(1)
      : null;
    const guestsTrend = lastWeekGuests > 0
      ? ((todayGuests - lastWeekGuests) / lastWeekGuests * 100).toFixed(1)
      : null;

    return {
      revenueTrend: revenueTrend !== null ? `${revenueTrend > 0 ? '+' : ''}${revenueTrend}%` : t("dashboard.basedOnHistory"),
      revenueTrendDirection: revenueTrend !== null ? (parseLocaleNumber(revenueTrend) >= 0 ? 'up' : 'down') : 'up',
      guestsTrend: guestsTrend !== null ? `${guestsTrend > 0 ? '+' : ''}${guestsTrend}%` : t("dashboard.basedOnHistory"),
      guestsTrendDirection: guestsTrend !== null ? (parseLocaleNumber(guestsTrend) >= 0 ? 'up' : 'down') : 'up',
    };
  };

  const trends = calculateTrends();

  // Use API forecast data for today's stat cards
  const forecastRevenue = todayForecast?.revenue || 0;

  // Today's actual costs from finance data (COGS + labor + other expenses + fixed proportional)
  const finance = useFinanceData();
  const todayDaily = finance.dailyData?.find((d) => d.date === today);
  const todayCosts = todayDaily?.costs || 0;
  // Forecast cost mirrors revenue forecast using historical cost-rate so costs are visible at parity
  const forecastCostRate = (finance.historicalCogsRate || 0) + (finance.historicalLaborRate || 0);
  const forecastCosts = forecastRevenue * forecastCostRate;

  return (
    <Layout>
      <div className="p-0 md:p-2 min-h-full">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-5 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-1 sm:mb-2">
              {t("dashboard.welcomeBack")}
            </h1>
            <p className="text-slate-500 text-lg">
              {format(new Date(), "EEEE, d. MMMM yyyy")}
            </p>
          </div>

          {/* Today: forecast row + actual row. Costs sit at parity with revenue. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6 mb-5 sm:mb-8">
            <StatCard
              title={t("dashboard.forecastRevenueToday")}
              value={`EUR ${forecastRevenue.toFixed(2)}`}
              icon={Calendar}
              trend="up"
              trendValue={t("dashboard.basedOnHistory")}
              bgColor="bg-cyan-500"
            />
            <StatCard
              title={t("dashboard.forecastCostsToday")}
              value={`EUR ${forecastCosts.toFixed(2)}`}
              icon={Receipt}
              trend="up"
              trendValue={t("dashboard.basedOnHistory")}
              bgColor="bg-amber-500"
            />
            <StatCard
              title={t("dashboard.guestsToday")}
              value={todayGuests}
              icon={Users}
              trend={trends.guestsTrendDirection}
              trendValue={trends.guestsTrend}
              bgColor="bg-blue-500"
            />
            <StatCard
              title={t("dashboard.revenueToday")}
              value={`EUR ${todayRevenue.toFixed(2)}`}
              icon={Euro}
              trend={trends.revenueTrendDirection}
              trendValue={trends.revenueTrend}
              bgColor="bg-green-500"
            />
            <StatCard
              title={t("dashboard.costsToday")}
              value={`EUR ${todayCosts.toFixed(2)}`}
              icon={Receipt}
              bgColor="bg-rose-500"
            />
          </div>

          {/* Main Grid - Revenue Forecast */}
          <div className="mb-6">
            <RevenueForecast />
          </div>

          {/* Secondary Grid */}
          <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
            <LowStockAlert inventoryItems={inventory} />
          </div>

          <div className="mt-6">
            <TaskSummary tasks={tasks} />
          </div>
        </div>
      </div>
    </Layout>
  );
}
